// Higher Self — the streaming conversation, ported from the handoff's higher-self-screen.jsx.
//
// The transcript is stored server-side (sealed) rather than only in this tab, so a conversation
// started on the laptop continues on the phone. Tokens arrive over SSE and render as they land; the
// finished reply is persisted once, at the end, so a dropped connection can't store half a sentence.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDEK, useSession } from '../auth/SessionProvider';
import {
  fetchConversations,
  fetchMessages,
  storeMessage,
  streamReply,
  weaveConversation,
  PERSONAS,
  type ChatMessage,
  type Conversation,
  type PersonaKey,
} from '../api/chat';
import { ApiError } from '../api/client';
import { Notice, Spinner } from '../components/ui';
import {
  IconArrow,
  IconExpand,
  IconLock,
  IconMenu,
  IconPlus,
  IconShrink,
  IconWave,
} from '../components/Icons';
import { ChatLibrary } from './ChatLibrary';
import { chatSuggestions } from './chat-suggestions';

// Docked open on a laptop, closed on a phone. The panel costs nothing beside a wide transcript and is
// how you find a conversation from last week; over a phone transcript it costs the whole screen.
const LIBRARY_KEY = 'oreia.chat.library';

function libraryDefault(): boolean {
  const saved = localStorage.getItem(LIBRARY_KEY);
  if (saved !== null) return saved === 'open';
  return window.matchMedia('(min-width: 1024px)').matches;
}

const PERSONA_KEY = 'oreia.persona';

// The composer starts at one line and grows with what is typed, up to six lines or so; past that it
// scrolls internally rather than eating the transcript. Measured in pixels because the height of a
// line depends on the reader's own type settings.
const COMPOSER_MAX_HEIGHT = 200;
/** Past roughly three lines, offer the full-height writing area. */
const COMPOSER_EXPANDABLE_HEIGHT = 92;

// Matches the Worker's own window (index.ts: `history.slice(-60)` inside a 24k-character budget).
// At 10 the browser was the limiter, not the model: a long conversation forgot its own opening, and
// the same thread read back shorter here than on the phone. The server still does the clamping, so
// sending its full window is safe — it drops the oldest turns if they don't fit.
const HISTORY_TURNS = 60;

/**
 * Which thread you are in is the URL, and /oreia is always a new one.
 *
 * This used to be a single id in localStorage, restored on arrival, plus a rule that silently adopted
 * the most recent real conversation whenever that thread turned out to be empty — a fix for iOS users
 * landing on a blank screen and reading it as data loss. Both are gone: reopening a conversation you
 * had finished with is the more common annoyance, and the blank screen is no longer blank. It is the
 * opening card, with every past thread one tap away in the library.
 *
 * A new thread earns its address by being spoken into, so a reload mid-conversation stays put and a
 * thread can be linked to, while a thread that is never used is never written down anywhere.
 */
function newConversationId(): string {
  return crypto.randomUUID();
}

export function OracleScreen() {
  const dek = useDEK();
  const { user } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { conversationId } = useParams();

  const [conversation, setConversation] = useState(() => conversationId ?? newConversationId());
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [threadsOpen, setThreadsOpen] = useState(libraryDefault);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [persona, setPersona] = useState<PersonaKey>(
    () => (localStorage.getItem(PERSONA_KEY) as PersonaKey | null) ?? 'higher_self',
  );
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isWeaving, setIsWeaving] = useState(false);
  const [weaveOpen, setWeaveOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const plusRef = useRef<HTMLDivElement>(null);
  /** The address this screen has already answered, so its own replace: true does not read as a move. */
  const routedRef = useRef(conversationId ?? null);

  const personaLabel = useMemo(
    () => PERSONAS.find((p) => p.key === persona)?.label ?? 'Higher Self',
    [persona],
  );

  const suggestions = useMemo(() => chatSuggestions(messages), [messages]);

  // Arriving from a card that was *about* something — Home's "Talk it through" — should not land in
  // an empty box that has forgotten what you tapped. The opening is placed in the composer rather
  // than sent, so the first thing said is still the user's own choice of words.
  useEffect(() => {
    const seeded = (location.state as { prompt?: string } | null)?.prompt;
    if (!seeded) return;

    setDraft((current) => (current.trim() ? current : seeded));
    // Cleared so going back and forward, or a reload, does not re-fill a box the user emptied.
    navigate('.', { replace: true, state: null });
  }, [location.state, navigate]);

  useEffect(() => {
    localStorage.setItem(PERSONA_KEY, persona);
  }, [persona]);

  useEffect(() => {
    const controller = new AbortController();
    fetchConversations(dek, controller.signal)
      .then((found) => {
        if (!controller.signal.aborted) setConversations(found);
      })
      .catch(() => {
        // The list is a convenience. Losing it must not stop the conversation itself from opening.
      })
      .finally(() => {
        if (!controller.signal.aborted) setThreadsLoading(false);
      });
    return () => controller.abort();
  }, [dek]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    fetchMessages(dek, conversation, controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) setMessages(loaded);
      })
      .catch(() => {
        // A conversation with no history yet is the normal first-run case, not an error.
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [dek, conversation]);

  /**
   * Follow the address bar: a named thread is opened, a bare /oreia is a new one.
   *
   * This is what makes tapping Oreia in the nav while already reading a conversation start over
   * rather than do nothing, and it is the same path the back button takes.
   */
  useEffect(() => {
    const routed = conversationId ?? null;
    if (routed === routedRef.current) return;
    routedRef.current = routed;

    if (routed) {
      setConversation(routed);
    } else {
      setMessages([]);
      setConversation(newConversationId());
    }
  }, [conversationId]);

  /**
   * The composer grows to fit what is in it, then stops and scrolls.
   *
   * Measured rather than guessed, because a line's height depends on the reader's type settings, and
   * it runs before paint so a message that wraps never appears clipped for a frame on the way in.
   */
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    if (expanded) {
      // Height belongs to the stylesheet in this mode — a tall panel, not a measurement.
      el.style.height = '';
      el.style.overflowY = 'auto';
      return;
    }

    el.style.height = 'auto';
    const natural = el.scrollHeight;
    el.style.height = `${Math.min(natural, COMPOSER_MAX_HEIGHT)}px`;
    el.style.overflowY = natural > COMPOSER_MAX_HEIGHT ? 'auto' : 'hidden';
    setCanExpand(natural > COMPOSER_EXPANDABLE_HEIGHT);
  }, [draft, expanded]);

  // A menu that stays open after you have looked away is a menu in the way. Pointer-down rather than
  // click so it closes on the same gesture that reaches for whatever is behind it.
  useEffect(() => {
    if (!weaveOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (!plusRef.current?.contains(event.target as Node)) setWeaveOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setWeaveOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [weaveOpen]);

  /**
   * Re-read the library. Called after an exchange completes rather than on every keystroke: a thread
   * started in this tab does not exist server-side until its first message lands, so without this it
   * would be missing from its own list until a reload. One request per exchange, seconds apart.
   */
  const refreshConversations = useCallback(() => {
    fetchConversations(dek)
      .then(setConversations)
      .catch(() => {
        /* The list is stale rather than wrong; it will catch up on the next exchange. */
      });
  }, [dek]);

  // A phone drawer covers the transcript, so picking from it has to dismiss it to show the result.
  // The docked panel is already beside the transcript: closing it there would hide the list the moment
  // you started using it, and skimming several conversations in a row is the point of having it.
  const dismissIfDrawer = useCallback(() => {
    if (window.matchMedia('(max-width: 1023px)').matches) setThreadsOpen(false);
  }, []);

  // Both go through the URL rather than setting state directly, so the effect above stays the single
  // place a thread is switched and the back button retraces the same steps.
  const openThread = useCallback(
    (id: string) => {
      dismissIfDrawer();
      navigate(`/oreia/${id}`);
    },
    [dismissIfDrawer, navigate],
  );

  const startThread = useCallback(() => {
    dismissIfDrawer();
    navigate('/oreia');
  }, [dismissIfDrawer, navigate]);

  useEffect(() => {
    localStorage.setItem(LIBRARY_KEY, threadsOpen ? 'open' : 'closed');
  }, [threadsOpen]);

  // Keep the newest turn in view as tokens arrive.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, streaming]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(async (override?: string) => {
    const prompt = (override ?? draft).trim();
    if (!prompt || isSending) return;

    const controller = new AbortController();
    abortRef.current = controller;

    setDraft('');
    // A sent message leaves an empty box, so the composer returns to the one line it started as.
    setExpanded(false);
    setWeaveOpen(false);
    setError(null);
    setIsSending(true);
    setStreaming('');

    const userTurn: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: prompt,
      createdAt: new Date(),
    };
    const history = [...messages, userTurn]
      .slice(-HISTORY_TURNS)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((current) => [...current, userTurn]);

    // The thread has been spoken into, so it earns its address. Replaced rather than pushed: Back
    // should leave Chat, not step out of the conversation you are in the middle of having.
    if (!conversationId) {
      routedRef.current = conversation;
      navigate(`/oreia/${conversation}`, { replace: true });
    }

    // Persist the user's turn immediately: it also feeds the extraction that builds their portrait,
    // and it should survive even if the reply fails.
    void storeMessage(conversation, 'user', prompt).catch(() => {
      // Best effort — the turn is already on screen and in the request we're about to make.
    });

    let reply = '';

    /**
     * Keep whatever arrived, whether the stream finished or the user stopped it.
     *
     * Stopping is not discarding: the tokens are already on screen, and the part someone chose to
     * stop at is usually the part they had finished reading. Dropping it would make Stop feel like
     * a mistake and would leave the phone showing a question with no answer.
     */
    const keepReply = () => {
      const text = reply.trim();
      if (!text) return;

      setMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), role: 'assistant', content: text, createdAt: new Date() },
      ]);
      void storeMessage(conversation, 'assistant', text).catch(() => {
        // The reply is on screen; a failed write just means it won't appear on the phone.
      });
    };

    try {
      await streamReply({
        prompt,
        persona,
        history,
        signal: controller.signal,
        onToken: (token) => {
          reply += token;
          setStreaming(reply);
        },
      });
      keepReply();
    } catch (err) {
      // An abort arrives here as a thrown error, so this is the only place a stopped reply can be
      // saved — and the only place we know the silence was deliberate rather than a failure.
      if (controller.signal.aborted) {
        keepReply();
      } else {
        setError(
          err instanceof ApiError && err.isPaymentRequired
            ? "You're out of energy for now. It refills over time, or you can upgrade for a larger monthly AI allowance."
            : err instanceof ApiError
              ? err.message
              : 'Oreia went quiet. Try again in a moment.',
        );
      }
    } finally {
      setStreaming('');
      setIsSending(false);
      abortRef.current = null;
      refreshConversations();
    }
  }, [draft, isSending, messages, persona, conversation, conversationId, navigate, refreshConversations]);

  /**
   * Weave the conversation into a journal entry.
   *
   * Only offered once both sides have actually said something — weaving a monologue produces an entry
   * that just repeats the prompt. The woven prose is handed to the composer rather than saved here,
   * so it can be read and edited before it joins the journal.
   */
  const weave = useCallback(
    async (mode: 'waking' | 'dream') => {
      if (isSending || isWeaving) return;

      setIsWeaving(true);
      setError(null);
      try {
        const woven = await weaveConversation(
          messages.map((m) => ({ role: m.role, content: m.content })),
          mode,
        );
        navigate('/write', {
          state: { seed: { title: woven.title, body: woven.body, kind: mode }, from: '/oreia' },
        });
      } catch (err) {
        setError(
          err instanceof ApiError && err.isPaymentRequired
            ? "You're out of energy for now, so this conversation can't be woven into an entry yet."
            : err instanceof ApiError
              ? err.message
              : 'That conversation could not be woven into an entry.',
        );
      } finally {
        setIsWeaving(false);
      }
    },
    [isSending, isWeaving, messages, navigate],
  );

  // A monologue is not a conversation, and weaving one produces an entry that only echoes the prompt.
  const canWeave =
    !isSending &&
    !isWeaving &&
    messages.some((m) => m.role === 'user' && m.content.trim()) &&
    messages.some((m) => m.role === 'assistant' && m.content.trim());

  const firstName = (user?.name || user?.email?.split('@')[0] || 'you').split(' ')[0];

  return (
    <div className="ochat">
      <div className="ohead">
        <div className="obrand">
          <span className="nm" style={{ fontSize: 22 }}>{personaLabel}</span>
        </div>
        {/* One control for the panel, at the right edge because that is the edge the panel comes
            from. It used to be two — a book icon on the left of the header to open, and a separate
            × inside the panel to close — which meant the way out was never where the way in had
            been. This is the only button, and it stays put: hamburger whether the drawer is open or
            closed, so the mark for "conversations" never becomes a different verb. */}
        <div className="ohead-actions">
          <div className="olock">
            <IconLock /> Private
          </div>
          <button
            className={`othreads-open${threadsOpen ? ' is-on' : ''}`}
            onClick={() => setThreadsOpen((open) => !open)}
            aria-expanded={threadsOpen}
            aria-label={
              threadsOpen
                ? 'Hide conversations'
                : conversations.length
                  ? `Your conversations (${conversations.length})`
                  : 'Your conversations'
            }
          >
            <IconMenu />
          </button>
        </div>
      </div>

      <ChatLibrary
        open={threadsOpen}
        conversations={conversations}
        currentId={conversation}
        loading={threadsLoading}
        onClose={() => setThreadsOpen(false)}
        onOpen={openThread}
        onNew={startThread}
      />

      <div className="ofilters" role="group" aria-label="Who you're talking to">
        {PERSONAS.map(({ key, label }) => (
          <button
            key={key}
            className={`ochip${persona === key ? ' active' : ''}`}
            onClick={() => setPersona(key)}
            aria-pressed={persona === key}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ochat-log" ref={scrollRef}>
        {isLoading ? (
          <Spinner label="Opening your conversation…" />
        ) : messages.length === 0 && !streaming ? (
          <div className="ocard ocard-bright" style={{ marginTop: 8 }}>
            <div className="ohead-row">
              <span className="olabel">{personaLabel}</span>
              <IconWave style={{ width: 20, height: 20, color: 'var(--ink-mute)' }} />
            </div>
            <h3 className="otitle" style={{ fontSize: 24 }}>
              I've read what you've written, {firstName}. <em>What's on your mind?</em>
            </h3>
            <p className="obody" style={{ marginTop: 10 }}>
              Your conversations are private to your account. Oreia uses AI services to respond.
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <Bubble key={message.id} role={message.role} content={message.content} />
            ))}
            {streaming ? <Bubble role="assistant" content={streaming} pending /> : null}
            {isSending && !streaming ? (
              <div className="obubble assistant">
                <Spinner label="Thinking…" />
              </div>
            ) : null}
          </>
        )}
      </div>

      {/* Openings, not decoration: a blank box is the hardest thing to answer, and once the
          conversation has a subject these follow it.
       *
       * Deliberately a sibling of the scrolling log rather than its last child. Inside the log they
       * scrolled with the transcript and got sliced in half at the scroll edge; here they are always
       * whole and always in the same place. Hidden mid-reply so they never shift under a thumb that
       * is reaching for Stop. */}
      {!isLoading && !isSending ? (
        <div className="osuggest" role="group" aria-label="Suggested openings">
          {suggestions.map((text) => (
            <button key={text} className="ochip osuggest-chip" onClick={() => void send(text)}>
              {text}
            </button>
          ))}
        </div>
      ) : null}

      {/* Outside the log for the same reason: a failure that scrolls away with the transcript is a
          failure the user never reads. */}
      {error ? (
        <div style={{ marginTop: 12 }}>
          <Notice tone="danger">{error}</Notice>
        </div>
      ) : null}

      <form
        className={`ochat-bar${expanded ? ' is-expanded' : ''}`}
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        {/* Weaving a conversation into the journal is one of the reasons to have the conversation, so
            it belongs where the writing happens rather than in a corner of the header where it was
            never found. */}
        <div className="oweave-menu" ref={plusRef}>
          <button
            className="ocompose-plus"
            type="button"
            onClick={() => setWeaveOpen((open) => !open)}
            aria-expanded={weaveOpen}
            aria-haspopup="menu"
            aria-label="Keep this conversation"
          >
            <IconPlus />
          </button>
          {weaveOpen ? (
            <div className="oweave-pop" role="menu">
              <button
                role="menuitem"
                disabled={!canWeave}
                onClick={() => { setWeaveOpen(false); void weave('waking'); }}
              >
                Weave into a waking entry
              </button>
              <button
                role="menuitem"
                disabled={!canWeave}
                onClick={() => { setWeaveOpen(false); void weave('dream'); }}
              >
                Weave into a dream entry
              </button>
              {!canWeave ? (
                <p className="oweave-hint">
                  Available once you and Oreia have both said something.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="ochat-field">
          <textarea
            ref={inputRef}
            className="oinput"
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter makes a new line — what people expect from a chat box.
              // Expanded, the box is for composing at length, so Enter is just a new line there.
              if (event.key === 'Enter' && !event.shiftKey && !expanded) {
                event.preventDefault();
                void send();
              }
            }}
            // Short on purpose. Chrome lays out the placeholder to measure an empty textarea, so a
            // placeholder that wraps on a phone opens the composer at two lines before a word is
            // typed. Which persona is listening is already the selected chip directly above.
            placeholder="Say anything…"
            aria-label="Your message"
          />
          {canExpand || expanded ? (
            <button
              className="ochat-expand"
              type="button"
              onClick={() => setExpanded((open) => !open)}
              aria-expanded={expanded}
              aria-label={expanded ? 'Shrink the writing area' : 'Expand the writing area'}
              title={expanded ? 'Shrink' : 'Expand'}
            >
              {expanded ? <IconShrink /> : <IconExpand />}
            </button>
          ) : null}
        </div>

        {isSending ? (
          <button
            className="ochat-send is-stop"
            type="button"
            onClick={() => abortRef.current?.abort()}
            aria-label="Stop generating"
            title="Stop"
          >
            <span className="ostop-glyph" aria-hidden />
          </button>
        ) : (
          <button className="ochat-send" type="submit" disabled={!draft.trim()} aria-label="Send">
            <IconArrow />
          </button>
        )}
      </form>
    </div>
  );
}

function Bubble({
  role,
  content,
  pending,
}: {
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
}) {
  return (
    <div className={`obubble ${role}`}>
      {content.split(/\n{2,}/).map((paragraph, i) => (
        <p key={i}>{paragraph}</p>
      ))}
      {pending ? <span className="ocaret" aria-hidden /> : null}
    </div>
  );
}
